import { useEffect, useState } from "react";
import axios from "axios";
import {
  Building2,
  FileText,
  Globe,
  Image as ImageIcon,
  Save,
  Plus,
  X,
} from "lucide-react";
import { canEditCompanyInfo } from "../../utils/permissions";
import { API_URL } from "../../api/api";
import { UPLOAD_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { toast } from "../../utils/toast";
import {
  SettingsCard,
  SettingsField,
  SettingsInput,
  SettingsSelect,
} from "./settingsUi";

const COUNTRY_STATE_MAP = {
  India: ["Kerala", "Tamil Nadu", "Karnataka", "Delhi"],
  USA: ["California", "Texas", "New York"],
  Canada: ["Ontario", "Quebec"],
};
const CURRENCIES = [
  { code: "INR", label: "₹ INR – Indian Rupee" },
  { code: "USD", label: "$ USD – US Dollar" },
  { code: "EUR", label: "€ EUR – Euro" },
  { code: "GBP", label: "£ GBP – British Pound" },
  { code: "AUD", label: "$ AUD – Australian Dollar" },
  { code: "CAD", label: "$ CAD – Canadian Dollar" },
  { code: "SGD", label: "$ SGD – Singapore Dollar" },
];
const TIMEZONES = [
  { value: "Asia/Kolkata", label: "(GMT+05:30) Mumbai, Kolkata, New Delhi" },
  { value: "Asia/Dubai", label: "(GMT+04:00) Dubai" },
  { value: "Europe/London", label: "(GMT+00:00) London" },
  { value: "Europe/Berlin", label: "(GMT+01:00) Berlin" },
  { value: "America/New_York", label: "(GMT-05:00) New York" },
  { value: "America/Chicago", label: "(GMT-06:00) Chicago" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) Los Angeles" },
  { value: "Asia/Singapore", label: "(GMT+08:00) Singapore" },
  { value: "Australia/Sydney", label: "(GMT+10:00) Sydney" },
];

const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 };
const textareaStyle = {
  width: "100%",
  padding: "11px 14px",
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  font: `400 14px ${T.font}`,
  color: T.fg1,
  outline: "none",
  boxSizing: "border-box",
  background: "var(--em-bg-card)",
  minHeight: 80,
  resize: "vertical",
};

const CompanyInformation = () => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  const editable = canEditCompanyInfo(role);

  const baseURL = `${API_URL}/api/settings/company-info`;
  const baseimg = `${UPLOAD_URL}`;
  const [form, setForm] = useState({
    company_name: "",
    company_email: "",
    phone: "",
    address: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    timezone: "",
    currency: "",
    gst_number: "",
    pan_number: "",
    tan_number: "",
    pf_number: "",
    esic_number: "",
    lin_number: "",
    registration_certificate_number: "",
    website: "",
    social_media: "",
  });
  const [errors, setErrors] = useState({});

  const [logo, setLogo] = useState(null);
  const [signature, setSignature] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [signaturePreview, setSignaturePreview] = useState("");
  const [socialLinks, setSocialLinks] = useState([{ platform: "", url: "" }]);

  /* ================= FETCH ================= */
  useEffect(() => {
    axios
      .get(baseURL, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const data = res.data || {};
        setForm((prev) => ({ ...prev, ...data }));
        if (data.company_logo) setLogoPreview(baseimg + data.company_logo);
        if (data.authorized_signature)
          setSignaturePreview(baseimg + data.authorized_signature);
        if (data.social_media) {
          if (typeof data.social_media === "string") {
            setSocialLinks(JSON.parse(data.social_media));
          } else if (Array.isArray(data.social_media)) {
            setSocialLinks(data.social_media);
          } else {
            setSocialLinks([]);
          }
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (form.country === "India" && !form.currency) {
      setForm((prev) => ({ ...prev, currency: "INR" }));
    }
  }, [form.country]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ================= HANDLERS ================= */
  // Fixed-length alphanumeric statutory identifiers, always issued upper case.
  const ID_FIELDS = {
    gst_number: { len: 15, label: "GST number" },
    pan_number: { len: 10, label: "PAN number" },
    tan_number: { len: 10, label: "TAN number" },
  };
  const alnumOfLength = (len) => new RegExp(`^[0-9A-Z]{${len}}$`);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Statutory ids accept letters and digits only, capped at their length.
    const next = ID_FIELDS[name]
      ? value
          .toUpperCase()
          .replace(/[^0-9A-Z]/g, "")
          .slice(0, ID_FIELDS[name].len)
      : value;
    if (name === "country") {
      setForm((prev) => ({ ...prev, country: next, state: "" }));
    } else {
      setForm((prev) => ({ ...prev, [name]: next }));
    }
    // Clear the field's error as soon as it is edited.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    setSignature(file);
    setSignaturePreview(URL.createObjectURL(file));
  };

  const addSocialLink = () =>
    setSocialLinks([...socialLinks, { platform: "", url: "" }]);
  const removeSocialLink = (index) =>
    setSocialLinks(socialLinks.filter((_, i) => i !== index));
  const updateSocialLink = (index, field, value) => {
    const updated = [...socialLinks];
    updated[index][field] = value;
    setSocialLinks(updated);
  };

  /* ================= VALIDATION ================= */
  // PAN/TAN/GST/PIN formats are Indian statutory formats, so those rules only
  // apply once the country is India. Everything in REQUIRED_BASIC is mandatory
  // regardless of country.
  const isIndia = form.country === "India";

  const REQUIRED_BASIC = [
    ["company_name", "Company name is required"],
    ["company_email", "Company email is required"],
    ["phone", "Company phone is required"],
    ["address", "Company address is required"],
    ["country", "Country is required"],
    ["state", "State / province is required"],
    ["city", "City is required"],
    ["postal_code", "Postal code / ZIP is required"],
  ];

  const validate = () => {
    const next = {};
    const val = (k) => String(form[k] ?? "").trim();

    REQUIRED_BASIC.forEach(([key, message]) => {
      if (!val(key)) next[key] = message;
    });

    if (val("company_email") && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val("company_email")))
      next.company_email = "Enter a valid email address";

    const digits = val("phone").replace(/\D/g, "");
    if (val("phone") && (digits.length < 7 || digits.length > 15))
      next.phone = "Enter a valid phone number";

    if (isIndia) {
      if (!val("pan_number"))
        next.pan_number = "PAN is mandatory for companies in India";
      if (!val("tan_number"))
        next.tan_number = "TAN is mandatory for processing payroll";
      if (val("postal_code") && !/^[1-9][0-9]{5}$/.test(val("postal_code")))
        next.postal_code = "PIN code must be 6 digits";
    }

    // GST / PAN / TAN: fixed-length alphanumeric. Checked in every country so
    // a malformed identifier is never stored. GST only binds on registered
    // companies, so it is validated when filled rather than required.
    Object.entries(ID_FIELDS).forEach(([key, { len, label }]) => {
      const value = val(key);
      if (!value || next[key]) return;
      if (!alnumOfLength(len).test(value))
        next[key] = `${label} must be exactly ${len} letters or digits`;
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    if (!validate()) {
      toast.error("Please complete the highlighted mandatory fields");
      return;
    }

    const fd = new FormData();
    Object.keys(form).forEach((k) => fd.append(k, form[k]));
    if (socialLinks) fd.append("social_media", JSON.stringify(socialLinks));
    if (logo) fd.append("company_logo", logo);
    if (signature) fd.append("authorized_signature", signature);
    try {
      await axios.put(baseURL, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Company information updated");
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error || "Could not update company information",
      );
    }
  };

  // `required` marks a hard block on save; `help` states the statutory
  // condition for the ones that only bind in certain cases.
  const LEGAL_FIELDS = [
    {
      name: "gst_number",
      label: "GST Number",
      ph: "32ABCDE1234F1Z5",
      max: ID_FIELDS.gst_number.len,
      help: "15 alphanumeric characters. Mandatory for GST-registered companies.",
    },
    {
      name: "pan_number",
      label: "PAN Number",
      ph: "ABCDE1234F",
      max: ID_FIELDS.pan_number.len,
      required: isIndia,
      help: "10 alphanumeric characters. Mandatory for all companies in India.",
    },
    {
      name: "tan_number",
      label: "TAN Number",
      ph: "ABCD12345E",
      max: ID_FIELDS.tan_number.len,
      required: isIndia,
      help: "10 alphanumeric characters. Mandatory for companies processing payroll.",
    },
    {
      name: "pf_number",
      label: "PF Number",
      ph: "PF Number",
      help: "Mandatory once employee strength exceeds 20.",
    },
    {
      name: "esic_number",
      label: "ESI Number",
      ph: "ESI Number",
      help: "Mandatory once employee strength exceeds 20.",
    },
    {
      name: "lin_number",
      label: "LIN Number",
      ph: "LIN Number",
      help: "Mandatory for certain states / regulations.",
    },
    {
      name: "registration_certificate_number",
      label: "Registration Certificate Number",
      ph: "CIN / Registration Certificate Number",
      full: true,
    },
  ];

  // Mandatory-field marker.
  const req = (label) => (
    <>
      {label} <span style={{ color: T.danger }}>*</span>
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* BASIC INFO */}
      <SettingsCard
        icon={Building2}
        title="Basic Information"
        subtitle="Core company details and location"
      >
        <div style={grid2}>
          <SettingsField
            label={req("Company Name")}
            error={errors.company_name}
          >
            <SettingsInput
              name="company_name"
              value={form.company_name}
              onChange={handleChange}
              disabled={!editable}
              invalid={!!errors.company_name}
              placeholder="Company Name"
            />
          </SettingsField>
          <SettingsField
            label={req("Company Email")}
            error={errors.company_email}
          >
            <SettingsInput
              type="email"
              name="company_email"
              value={form.company_email || ""}
              onChange={handleChange}
              disabled={!editable}
              invalid={!!errors.company_email}
              placeholder="info@company.com"
            />
          </SettingsField>
          <SettingsField label={req("Company Phone")} error={errors.phone}>
            <SettingsInput
              name="phone"
              value={form.phone || ""}
              onChange={handleChange}
              disabled={!editable}
              invalid={!!errors.phone}
              placeholder="+91 98765 43210"
            />
          </SettingsField>
          <SettingsField label={req("Address")} error={errors.address}>
            <textarea
              name="address"
              value={form.address || ""}
              onChange={handleChange}
              disabled={!editable}
              placeholder="Address"
              style={{
                ...textareaStyle,
                borderColor: errors.address ? T.danger : T.border,
              }}
            />
          </SettingsField>
          <SettingsField label={req("Country")} error={errors.country}>
            <SettingsSelect
              name="country"
              value={form.country}
              onChange={handleChange}
              disabled={!editable}
            >
              <option value="">Select Country</option>
              {Object.keys(COUNTRY_STATE_MAP).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
          <SettingsField label={req("State / Province")} error={errors.state}>
            <SettingsSelect
              name="state"
              value={form.state}
              onChange={handleChange}
              disabled={!editable || !form.country}
            >
              <option value="">Select State</option>
              {(COUNTRY_STATE_MAP[form.country] || []).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
          <SettingsField label={req("District / City")} error={errors.city}>
            <SettingsInput
              name="city"
              value={form.city}
              onChange={handleChange}
              disabled={!editable}
              invalid={!!errors.city}
              placeholder="City"
            />
          </SettingsField>
          <SettingsField
            label={req("Postal Code / ZIP")}
            error={errors.postal_code}
          >
            <SettingsInput
              name="postal_code"
              value={form.postal_code || ""}
              onChange={handleChange}
              disabled={!editable}
              invalid={!!errors.postal_code}
              placeholder={isIndia ? "682030" : "Postal Code / ZIP"}
            />
          </SettingsField>
          <SettingsField label="Timezone">
            <SettingsSelect
              name="timezone"
              value={form.timezone}
              onChange={handleChange}
              disabled={!editable}
            >
              <option value="">Select timezone</option>
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
          <SettingsField label="Currency">
            <SettingsSelect
              name="currency"
              value={form.currency}
              onChange={handleChange}
              disabled={!editable}
            >
              <option value="">Select currency</option>
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </SettingsSelect>
          </SettingsField>
        </div>
      </SettingsCard>

      {/* LEGAL & REGISTRATION DETAILS */}
      <SettingsCard
        icon={FileText}
        title="Legal & Registration Details"
        subtitle="Regulatory and compliance information"
      >
        <div style={grid2}>
          {LEGAL_FIELDS.map((f) => (
            <SettingsField
              key={f.name}
              label={f.required ? req(f.label) : f.label}
              help={f.help}
              error={errors[f.name]}
              style={f.full ? { gridColumn: "1 / -1" } : undefined}
            >
              <SettingsInput
                name={f.name}
                placeholder={f.ph}
                value={form[f.name] || ""}
                onChange={handleChange}
                disabled={!editable}
                invalid={!!errors[f.name]}
                maxLength={f.max}
                autoCapitalize={f.max ? "characters" : undefined}
                spellCheck={f.max ? false : undefined}
              />
            </SettingsField>
          ))}
        </div>
      </SettingsCard>

      {/* ONLINE PRESENCE */}
      <SettingsCard
        icon={Globe}
        title="Online Presence"
        subtitle="Website and social media links"
      >
        <SettingsField label="Website URL">
          <SettingsInput
            name="website"
            value={form.website}
            onChange={handleChange}
            disabled={!editable}
            placeholder="Website URL"
          />
        </SettingsField>

        <div
          style={{
            font: `600 13px ${T.font}`,
            color: T.fg1,
            margin: "4px 0 10px",
          }}
        >
          Social Media
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {socialLinks.map((row, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 10, alignItems: "center" }}
            >
              <SettingsInput
                placeholder="Platform (LinkedIn, X, Facebook)"
                value={row.platform}
                disabled={!editable}
                onChange={(e) =>
                  updateSocialLink(i, "platform", e.target.value)
                }
                style={{ flex: "0 0 240px" }}
              />
              <SettingsInput
                placeholder="URL"
                value={row.url}
                disabled={!editable}
                onChange={(e) => updateSocialLink(i, "url", e.target.value)}
                style={{ flex: 1 }}
              />
              {editable && (
                <button
                  onClick={() => removeSocialLink(i)}
                  style={{
                    width: 38,
                    height: 38,
                    flexShrink: 0,
                    borderRadius: 9,
                    border: `1px solid ${T.border}`,
                    background: "var(--em-bg-card)",
                    cursor: "pointer",
                    color: T.danger,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <X size={15} />
                </button>
              )}
            </div>
          ))}
          {editable && (
            <button
              onClick={addSocialLink}
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 14px",
                borderRadius: 8,
                border: `1px solid ${T.borderSoft}`,
                background: "var(--em-bg-card)",
                color: T.fg1,
                font: `500 13px ${T.font}`,
                cursor: "pointer",
                marginTop: 2,
              }}
            >
              <Plus size={15} /> Add Social Link
            </button>
          )}
        </div>
      </SettingsCard>

      {/* BRANDING */}
      <SettingsCard
        icon={ImageIcon}
        title="Branding Assets"
        subtitle="Company logo and signature"
      >
        <div style={grid2}>
          <SettingsField label="Company Logo">
            {logoPreview && (
              <img
                src={logoPreview}
                alt="Logo"
                style={{
                  maxHeight: 90,
                  marginBottom: 10,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  padding: 8,
                  background: "var(--em-bg-card)",
                  display: "block",
                }}
              />
            )}
            {editable && (
              <input
                type="file"
                onChange={handleLogoChange}
                style={{ font: `400 13px ${T.font}` }}
              />
            )}
          </SettingsField>
          <SettingsField label="Authorized Signature">
            {signaturePreview && (
              <img
                src={signaturePreview}
                alt="Signature"
                style={{
                  maxHeight: 90,
                  marginBottom: 10,
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  padding: 8,
                  background: "var(--em-bg-card)",
                  display: "block",
                }}
              />
            )}
            {editable && (
              <input
                type="file"
                onChange={handleSignatureChange}
                style={{ font: `400 13px ${T.font}` }}
              />
            )}
          </SettingsField>
        </div>
      </SettingsCard>

      {/* SAVE BAR */}
      {editable && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            background: T.bgCard,
            border: `1px solid ${T.orangeEdge}`,
            borderRadius: T.rXl,
            padding: "18px 24px",
            boxShadow: "0 6px 16px -10px rgba(245,75,1,0.4)",
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
              Ready to save changes?
            </div>
            <div
              style={{ font: `400 13px ${T.font}`, color: T.fg5, marginTop: 2 }}
            >
              All configurations will be updated
            </div>
          </div>
          <button
            onClick={handleSave}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 20px",
              borderRadius: 10,
              border: 0,
              cursor: "pointer",
              background: T.gradient,
              color: "#fff",
              font: `700 14px ${T.font}`,
              boxShadow: T.shadowBrand,
            }}
          >
            <Save size={16} /> Save Changes
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanyInformation;
